/**
 * Connected components and morphological erosion over a binary mask.
 *
 * Both passes are written for a full-resolution scan: an A3 bed at 300 dpi is
 * ~4.4 MP, so every buffer here is a typed array sized once, and the flood fill
 * is an explicit BFS queue. A recursive flood fill overflows the JS stack on a
 * raster this size — one large piece is hundreds of thousands of pixels deep in
 * the recursion — which is why the queue is an `Int32Array` and never a call
 * stack.
 */

/** Result of one labelling pass. Component ids are 1-based; 0 means "not in the mask". */
export interface LabeledComponents {
  /** `labels[y * width + x]` — 0 for pixels outside the mask, else a 1-based component id. */
  labels: Int32Array
  /** Number of components found. Valid ids are `1 … count`. */
  count: number
  /** Pixel count per component, indexed by id (slot 0 unused). */
  sizes: Int32Array
  /** Inclusive bounding boxes per component, indexed by id (slot 0 unused). */
  minX: Int32Array
  minY: Int32Array
  maxX: Int32Array
  maxY: Int32Array
  /** 1 when any pixel of the component sits on the raster edge. */
  touchesBorder: Uint8Array
  /** Raster index of the component's first pixel in scan order — the tracing seed. */
  seeds: Int32Array
}

/** 8-neighbour offsets, clockwise from east. Shared with the boundary tracer. */
export const NEIGHBOR_DX = [1, 1, 0, -1, -1, -1, 0, 1] as const
export const NEIGHBOR_DY = [0, 1, 1, 1, 0, -1, -1, -1] as const

/** Component-array capacity a fresh labelling starts with; it doubles as needed. */
const INITIAL_COMPONENT_CAPACITY = 256

/** Reallocates a component array to at least `capacity`, preserving its contents. */
function growInt32(array: Int32Array, capacity: number): Int32Array<ArrayBuffer> {
  const next = new Int32Array(capacity)
  next.set(array)
  return next
}

/**
 * Labels the set pixels of `mask` with an iterative BFS flood fill.
 *
 * `connectivity` must be 4 or 8. Foreground is labelled 8-connected and
 * background 4-connected in this module: the pairing is not cosmetic, it is the
 * only combination under which "a background component that does not touch the
 * frame is a hole of exactly one foreground component" is true. With both sets
 * 8-connected a diagonal pixel chain would both enclose a hole and let the
 * background leak through it.
 *
 * The first pixel of each component in raster order is recorded as its seed:
 * it is the topmost-then-leftmost pixel of the component, so its west neighbour
 * is guaranteed to be outside the component — the precondition Moore-neighbour
 * tracing needs for its initial backtrack.
 */
export function labelComponents(
  mask: Uint8Array,
  width: number,
  height: number,
  connectivity: 4 | 8,
): LabeledComponents {
  const pixels = width * height
  const labels = new Int32Array(pixels)
  const queue = new Int32Array(pixels)

  // Per-component arrays grow on demand. A scan holds a handful of pieces but a
  // noisy one can hold thousands of specks, and sizing these by pixel count "to
  // be safe" would cost 100 MB of dead buffer on a 4 MP scan — inside a browser
  // tab that is already holding the bitmap and the canvas.
  let capacity = INITIAL_COMPONENT_CAPACITY
  let sizes = new Int32Array(capacity)
  let minX = new Int32Array(capacity)
  let minY = new Int32Array(capacity)
  let maxX = new Int32Array(capacity)
  let maxY = new Int32Array(capacity)
  let touchesBorder = new Int32Array(capacity)
  let seeds = new Int32Array(capacity)

  const steps = connectivity === 8 ? 8 : 4
  // The 4-connected walk uses every other offset (E, S, W, N).
  const stride = connectivity === 8 ? 1 : 2

  let count = 0
  for (let start = 0; start < pixels; start += 1) {
    if (mask[start] !== 1 || labels[start] !== 0) continue

    count += 1
    const id = count
    if (id >= capacity) {
      capacity *= 2
      sizes = growInt32(sizes, capacity)
      minX = growInt32(minX, capacity)
      minY = growInt32(minY, capacity)
      maxX = growInt32(maxX, capacity)
      maxY = growInt32(maxY, capacity)
      touchesBorder = growInt32(touchesBorder, capacity)
      seeds = growInt32(seeds, capacity)
    }
    labels[start] = id
    seeds[id] = start

    let head = 0
    let tail = 0
    queue[tail] = start
    tail += 1

    let size = 0
    let bx0 = width
    let by0 = height
    let bx1 = -1
    let by1 = -1
    let border = 0

    while (head < tail) {
      const index = queue[head]!
      head += 1
      const x = index % width
      const y = (index - x) / width

      size += 1
      if (x < bx0) bx0 = x
      if (y < by0) by0 = y
      if (x > bx1) bx1 = x
      if (y > by1) by1 = y
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) border = 1

      for (let k = 0; k < steps; k += 1) {
        const dir = k * stride
        const nx = x + NEIGHBOR_DX[dir]!
        const ny = y + NEIGHBOR_DY[dir]!
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue
        const n = ny * width + nx
        if (mask[n] !== 1 || labels[n] !== 0) continue
        labels[n] = id
        queue[tail] = n
        tail += 1
      }
    }

    sizes[id] = size
    minX[id] = bx0
    minY[id] = by0
    maxX[id] = bx1
    maxY[id] = by1
    touchesBorder[id] = border
  }

  const used = count + 1
  return {
    labels,
    count,
    sizes: sizes.slice(0, used),
    minX: minX.slice(0, used),
    minY: minY.slice(0, used),
    maxX: maxX.slice(0, used),
    maxY: maxY.slice(0, used),
    touchesBorder: Uint8Array.from(touchesBorder.subarray(0, used)),
    seeds: seeds.slice(0, used),
  }
}

/**
 * Erodes `mask` by a square structuring element of the given radius, as two
 * separable box passes (horizontal, then vertical).
 *
 * A square is separable, so eroding by a `(2r+1)` run horizontally and then
 * vertically is exactly erosion by the `(2r+1)²` square — at O(pixels) instead
 * of O(pixels · r²). Each 1-D pass keeps, for every pixel, the length of the
 * set run ending at it and the length starting at it; a pixel survives when
 * both are at least `r + 1`, which is the same statement as "all `2r+1` pixels
 * centred on it are set".
 *
 * Outside the raster counts as background, so the frame erodes inwards too.
 * That is the wanted behaviour for the neck test, where the mask window is cut
 * tight around one shape.
 */
export function erodeMask(mask: Uint8Array, width: number, height: number, radius: number): Uint8Array {
  if (radius <= 0) return mask.slice()

  const horizontal = new Uint8Array(mask.length)
  const runBefore = new Int32Array(Math.max(width, height))
  const runAfter = new Int32Array(Math.max(width, height))

  for (let y = 0; y < height; y += 1) {
    const row = y * width
    let run = 0
    for (let x = 0; x < width; x += 1) {
      run = mask[row + x] === 1 ? run + 1 : 0
      runBefore[x] = run
    }
    run = 0
    for (let x = width - 1; x >= 0; x -= 1) {
      run = mask[row + x] === 1 ? run + 1 : 0
      runAfter[x] = run
    }
    for (let x = 0; x < width; x += 1) {
      horizontal[row + x] = runBefore[x]! > radius && runAfter[x]! > radius ? 1 : 0
    }
  }

  const out = new Uint8Array(mask.length)
  for (let x = 0; x < width; x += 1) {
    let run = 0
    for (let y = 0; y < height; y += 1) {
      run = horizontal[y * width + x] === 1 ? run + 1 : 0
      runBefore[y] = run
    }
    run = 0
    for (let y = height - 1; y >= 0; y -= 1) {
      run = horizontal[y * width + x] === 1 ? run + 1 : 0
      runAfter[y] = run
    }
    for (let y = 0; y < height; y += 1) {
      out[y * width + x] = runBefore[y]! > radius && runAfter[y]! > radius ? 1 : 0
    }
  }

  return out
}
